import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { MenuRoute } from '../Menu/types';
import { MenuContext, MenuContextType } from './MenuContext';
import { NavigationDirection, NavigationRequest } from './hooks';

// Menu Provider - manages navigation state globally
export const MenuProvider = ({
    children,
    routes: providedRoutes = [],
}: {
    children: ReactNode;
    routes?: MenuRoute[];
}) => {
    const [path, setPath] = useState<string[]>([]);
    const [viewStack, setViewStack] = useState<(ReactNode | null)[]>([]);
    const [navigationRequest, setNavigationRequest] = useState<NavigationRequest | null>(null);
    const [direction, setDirection] = useState<NavigationDirection>('forwards');
    const [animationKey, setAnimationKey] = useState(0);
    const [routes, setRoutes] = useState<MenuRoute[]>(providedRoutes);
    const hasRegisteredRoutes = useRef(false);

    // Update routes when providedRoutes change (only on initial mount or if routes haven't been registered yet)
    React.useEffect(() => {
        if (providedRoutes.length > 0 && !hasRegisteredRoutes.current) {
            setRoutes(providedRoutes);
        }
    }, [providedRoutes]);

    const activeView = viewStack[viewStack.length - 1] ?? null;

    // Create lookup maps for routes
    const routesById = useMemo(() => {
        const map = new Map<string, MenuRoute>();
        routes.forEach(route => {
            map.set(route.id, route);
        });
        return map;
    }, [routes]);

    // Register routes function
    const registerRoutes = React.useCallback((newRoutes: MenuRoute[]) => {
        hasRegisteredRoutes.current = true;
        setRoutes(newRoutes);
    }, []);

    // Helper: Get route by id
    const getRouteById = React.useCallback(
        (id: string): MenuRoute | undefined => {
            return routesById.get(id);
        },
        [routesById]
    );

    // Helper: Get routes matching a specific path
    const getRoutesByPath = React.useCallback(
        (targetPath: string[]): MenuRoute[] => {
            return routes.filter(route => {
                if (route.path.length !== targetPath.length) return false;
                return route.path.every((segment, index) => segment === targetPath[index]);
            });
        },
        [routes]
    );

    // Helper: Get child routes for a given parent path
    const getChildRoutes = React.useCallback(
        (parentPath: string[]): MenuRoute[] => {
            return routes.filter(route => {
                if (route.path.length !== parentPath.length + 1) return false;
                return parentPath.every((segment, index) => segment === route.path[index]);
            });
        },
        [routes]
    );

    const navigateTo = React.useCallback((label: string, view?: ReactNode) => {
        setDirection('forwards');
        setPath(prev => [...prev, label]);
        setViewStack(prev => [...prev, view ?? null]);
    }, []);

    const navigateByPath = React.useCallback(
        (targetPath: string[]) => {
            // Find route matching the path
            const route = getRoutesByPath(targetPath)[0];
            if (!route) {
                console.warn(`Route with path "${targetPath.join('/')}" not found`);
                return;
            }

            // Reset navigation hierarchy when navigating by path
            setPath([]);
            setViewStack([]);
            setDirection('forwards');

            // Build path from route path segments
            const newPath: string[] = [];
            const newViewStack: (ReactNode | null)[] = [];

            // Navigate through each segment in the path
            for (let i = 0; i < targetPath.length; i++) {
                const segmentPath = targetPath.slice(0, i + 1);
                const segmentRoute = getRoutesByPath(segmentPath)[0];
                if (segmentRoute) {
                    newPath.push(segmentRoute.label);
                    // Only add view for the final segment
                    if (i === targetPath.length - 1) {
                        const view =
                            typeof segmentRoute.view === 'function' ? segmentRoute.view() : (segmentRoute.view ?? null);
                        newViewStack.push(view);
                    } else {
                        newViewStack.push(null);
                    }
                }
            }

            setPath(newPath);
            setViewStack(newViewStack);
        },
        [getRoutesByPath]
    );

    const navigateById = React.useCallback(
        (id: string, ...args: unknown[]) => {
            const route = getRouteById(id);
            if (!route) {
                console.warn(`Route with id "${id}" not found`);
                return;
            }

            // Reset navigation hierarchy when navigating by ID
            setPath([]);
            setViewStack([]);
            setDirection('forwards');

            // Build path from route path segments
            const newPath: string[] = [];
            const newViewStack: (ReactNode | null)[] = [];

            // Navigate through each segment in the route's path
            for (let i = 0; i < route.path.length; i++) {
                const segmentPath = route.path.slice(0, i + 1);
                const segmentRoute = getRoutesByPath(segmentPath)[0];
                if (segmentRoute) {
                    newPath.push(segmentRoute.label);
                    // Only add view for the final segment
                    if (i === route.path.length - 1) {
                        const view =
                            typeof route.view === 'function'
                                ? route.view((args[0] as number) ?? undefined)
                                : (route.view ?? null);
                        newViewStack.push(view);
                    } else {
                        newViewStack.push(null);
                    }
                }
            }

            // If route has no path segments, just navigate to it directly
            if (route.path.length === 0) {
                const view =
                    typeof route.view === 'function'
                        ? route.view((args[0] as number) ?? undefined)
                        : (route.view ?? null);
                navigateTo(route.label, view);
            } else {
                setPath(newPath);
                setViewStack(newViewStack);
            }
        },
        [getRouteById, getRoutesByPath, navigateTo]
    );

    // Handle navigationRequest (programmatic navigation)
    React.useEffect(() => {
        if (navigationRequest) {
            navigateTo(navigationRequest.label, navigationRequest.view);
            // Clear navigationRequest after handling it
            setNavigationRequest(null);
        }
    }, [navigationRequest, navigateTo]);

    const goBack = React.useCallback(() => {
        if (path.length === 0) {
            return;
        }

        setDirection('backwards');
        setPath(prev => prev.slice(0, -1));
        setViewStack(prev => prev.slice(0, -1));
        setAnimationKey(prev => prev + 1);
    }, [path.length]);

    const reset = React.useCallback(() => {
        setDirection('backwards');
        setPath([]);
        setViewStack([]);
    }, []);

    const contextValue: MenuContextType = {
        path,
        activeView,
        routes,
        routesById,
        navigateTo,
        navigateById,
        navigateByPath,
        setNavigationRequest,
        navigationRequest,
        goBack,
        reset,
        direction,
        animationKey,
        getRouteById,
        getRoutesByPath,
        getChildRoutes,
        registerRoutes,
    };

    return <MenuContext.Provider value={contextValue}>{children}</MenuContext.Provider>;
};
